import React, { createContext, useContext, useState, ReactNode } from "react";
import { CartItem, Product } from "@/types/product";
import { useToast } from "@/hooks/use-toast";

interface CartContextType {
  items: CartItem[];
  addToCart: (product: Product, variant?: { id: string; label: string; price: number; stock: number }) => void;
  removeFromCart: (productId: string, variantId?: string) => void;
  updateQuantity: (productId: string, quantity: number, variantId?: string) => void;
  clearCart: () => void;
  totalItems: number;
  subtotal: number;
  gstAmount: number;
  total: number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

/** Unique key for a cart item (product + optional variant) */
const cartKey = (productId: string, variantId?: string) =>
  variantId ? `${productId}__${variantId}` : productId;

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const [items, setItems] = useState<CartItem[]>([]);
  const { toast } = useToast();

  const addToCart = (product: Product, variant?: { id: string; label: string; price: number; stock: number }) => {
    setItems((prev) => {
      const key = cartKey(product.id, variant?.id);
      const existing = prev.find((i) => cartKey(i.product.id, i.variantId) === key);
      if (existing) {
        return prev.map((i) =>
          cartKey(i.product.id, i.variantId) === key ? { ...i, quantity: i.quantity + 1 } : i
        );
      }
      return [
        ...prev,
        {
          product,
          quantity: 1,
          variantId: variant?.id,
          variantLabel: variant?.label,
          variantPrice: variant?.price,
          variantStock: variant?.stock,
        },
      ];
    });
    toast({ title: "Added to cart", description: variant?.label ? `${product.name} (${variant.label})` : product.name });
  };

  const removeFromCart = (productId: string, variantId?: string) => {
    const key = cartKey(productId, variantId);
    setItems((prev) => prev.filter((i) => cartKey(i.product.id, i.variantId) !== key));
  };

  const updateQuantity = (productId: string, quantity: number, variantId?: string) => {
    if (quantity <= 0) {
      removeFromCart(productId, variantId);
      return;
    }
    const key = cartKey(productId, variantId);
    setItems((prev) =>
      prev.map((i) => (cartKey(i.product.id, i.variantId) === key ? { ...i, quantity } : i))
    );
  };

  const clearCart = () => setItems([]);

  const totalItems = items.reduce((sum, i) => sum + i.quantity, 0);

  const getItemPrice = (i: CartItem) => i.variantPrice ?? i.product.price;

  const subtotal = items.reduce((sum, i) => {
    const price = getItemPrice(i);
    const basePrice = (price * 100) / (100 + i.product.gstRate);
    return sum + basePrice * i.quantity;
  }, 0);

  const gstAmount = items.reduce((sum, i) => {
    const price = getItemPrice(i);
    const basePrice = (price * 100) / (100 + i.product.gstRate);
    const gst = price - basePrice;
    return sum + gst * i.quantity;
  }, 0);

  const total = items.reduce((sum, i) => sum + getItemPrice(i) * i.quantity, 0);

  return (
    <CartContext.Provider
      value={{ items, addToCart, removeFromCart, updateQuantity, clearCart, totalItems, subtotal, gstAmount, total }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
